import { useState } from "react";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { PDFDocument, rgb } from "pdf-lib";
import { ToolContainer, downloadBlob } from "@/components/ToolLayout";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Plus, Trash2, Download, FlaskConical, Eraser } from "lucide-react";
import { toast } from "sonner";
import { ToolInfoSection } from "@/components/ToolInfoSection";
import { useLogUsageOnAction } from "@/hooks/useLogUsageOnAction";
import { CURRENCY_CHOICES, fmtMoney } from "@/tools/converters/money";
import { invoiceErrors, invoiceTotals, addDaysIso, nextInvoiceNumber, termsLine, TERMS_DAYS, type PaymentTerms } from "./invoiceCalc";
import { TextKit } from "./pdfText";
import { todayIso } from "./legalClauses";

interface LineItem {
  id: string;
  description: string;
  quantity: string;
  unitPrice: string;
  credit?: boolean;
}

interface FormState {
  businessName: string; businessAddress: string; businessTaxId: string; clientName: string; clientAddress: string;
  invoiceNumber: string; invoiceDate: string; dueDate: string; terms: PaymentTerms; currency: string;
  discountPct: string; taxPct: string; taxLabel: string; shipping: string; paid: string; notes: string;
  items: LineItem[]; includeSignature: boolean;
}

// Last invoice number made in this browser tab only (sessionStorage is erased when the tab closes).
const LAST_KEY = "fdp-invoice-last-number";
const readLast = (): string | null => { try { return sessionStorage.getItem(LAST_KEY); } catch { return null; } };
const writeLast = (v: string) => { try { sessionStorage.setItem(LAST_KEY, v); } catch { /* fine without it */ } };

const SAMPLE: Omit<FormState, "invoiceNumber" | "invoiceDate" | "dueDate"> = {
  businessName: "Northwind Design Studio", businessAddress: "12 Market Street\nPortland, OR 97201", businessTaxId: "",
  clientName: "Acme Corp", clientAddress: "500 Main Street\nSeattle, WA 98101", terms: "net30", currency: "USD",
  discountPct: "5", taxPct: "8", taxLabel: "Tax", shipping: "", paid: "100", notes: "Payment by bank transfer within 30 days. Thank you!",
  items: [
    { id: "s1", description: "Brand identity design", quantity: "1", unitPrice: "1200" },
    { id: "s2", description: "Website mockups (hours)", quantity: "6", unitPrice: "85" },
  ],
  includeSignature: false,
};

const TOOL_INFO_CONTENT = {
  what: `Creates a professional invoice with your business details, line items, discount, tax and totals in the currency you choose, ready to send to a client as a PDF.`,
  how: `Enter your business and client details, add line items with quantities and prices, and set a currency, discount and tax rate if you need them. Long invoices continue on extra pages. Download the finished invoice as a PDF. Names and notes in any language work, including accents, ₹ and Hindi.`,
  tip: `Keep a consistent invoice numbering system from the start. It makes bookkeeping and tracking which invoices have been paid much easier once you have more than a handful. For India, put your GSTIN in the tax ID box; for the EU, your VAT number.`,
};

const PAGE_W = 612, PAGE_H = 792, LEFT = 50, RIGHT = 562, BOTTOM = 70;
const GREY = rgb(0.45, 0.45, 0.45);

export default function InvoiceGenerator() {
  const [businessName, setBusinessName] = useState("");
  const [businessAddress, setBusinessAddress] = useState("");
  const [businessTaxId, setBusinessTaxId] = useState("");
  const [clientName, setClientName] = useState("");
  const [clientAddress, setClientAddress] = useState("");
  const [invoiceNumber, setInvoiceNumber] = useState(() => nextInvoiceNumber(readLast()));
  const [invoiceDate, setInvoiceDate] = useState(todayIso());
  const [dueDate, setDueDate] = useState("");
  const [terms, setTerms] = useState<PaymentTerms>("custom");
  const [shipping, setShipping] = useState("");
  const [paid, setPaid] = useState("");
  const [currency, setCurrency] = useState("USD");
  const [discountPct, setDiscountPct] = useState("");
  const [taxPct, setTaxPct] = useState("");
  const [taxLabel, setTaxLabel] = useState("Tax");
  const [notes, setNotes] = useState("");
  const [items, setItems] = useState<LineItem[]>([{ id: "1", description: "", quantity: "1", unitPrice: "" }]);
  const [includeSignature, setIncludeSignature] = useState(false);
  const [attempted, setAttempted] = useState(false);
  const [confirm, setConfirm] = useState<"sample" | "clear" | null>(null);

  const snapshot = (): FormState => ({ businessName, businessAddress, businessTaxId, clientName, clientAddress, invoiceNumber, invoiceDate, dueDate, terms, currency, discountPct, taxPct, taxLabel, shipping, paid, notes, items, includeSignature });
  const apply = (f: FormState) => {
    setBusinessName(f.businessName); setBusinessAddress(f.businessAddress); setBusinessTaxId(f.businessTaxId); setClientName(f.clientName); setClientAddress(f.clientAddress);
    setInvoiceNumber(f.invoiceNumber); setInvoiceDate(f.invoiceDate); setDueDate(f.dueDate); setTerms(f.terms); setCurrency(f.currency);
    setDiscountPct(f.discountPct); setTaxPct(f.taxPct); setTaxLabel(f.taxLabel); setShipping(f.shipping); setPaid(f.paid); setNotes(f.notes);
    setItems(f.items); setIncludeSignature(f.includeSignature); setAttempted(false);
  };
  const hasData = !!(businessName || clientName || businessAddress || clientAddress || businessTaxId || notes || shipping || paid || discountPct || taxPct || items.some(i => i.description || i.unitPrice));
  const withUndo = (message: string, make: () => FormState) => {
    const before = snapshot();
    apply(make());
    toast(message, { action: { label: "Undo", onClick: () => apply(before) }, duration: 8000 });
  };
  const doSample = () => withUndo("Sample invoice loaded.", () => {
    const d = todayIso();
    return { ...SAMPLE, invoiceNumber, invoiceDate: d, dueDate: addDaysIso(d, 30), items: SAMPLE.items.map(i => ({ ...i })) };
  });
  const doClear = () => withUndo("Invoice cleared.", () => ({
    businessName: "", businessAddress: "", businessTaxId: "", clientName: "", clientAddress: "", invoiceNumber, invoiceDate: todayIso(), dueDate: "", terms: "custom", currency: "USD",
    discountPct: "", taxPct: "", taxLabel: "Tax", shipping: "", paid: "", notes: "", items: [{ id: Date.now().toString(), description: "", quantity: "1", unitPrice: "" }], includeSignature: false,
  }));
  const ask = (kind: "sample" | "clear") => { if (hasData) setConfirm(kind); else (kind === "sample" ? doSample : doClear)(); };

  const changeInvoiceDate = (v: string) => {
    setInvoiceDate(v);
    if (terms !== "custom") setDueDate(addDaysIso(v, TERMS_DAYS[terms]));
  };
  const changeTerms = (v: PaymentTerms) => {
    setTerms(v);
    if (v !== "custom") setDueDate(addDaysIso(invoiceDate, TERMS_DAYS[v]));
  };

  const addItem = () => setItems(prev => [...prev, { id: Date.now().toString(), description: "", quantity: "1", unitPrice: "" }]);
  const removeItem = (id: string) => setItems(prev => prev.filter(i => i.id !== id));
  const updateItem = (id: string, field: keyof LineItem, value: string) =>
    setItems(prev => prev.map(i => (i.id === id ? { ...i, [field]: value } : i)));

  const form = { businessName, clientName, invoiceDate, dueDate, items, discountPct, taxPct, shipping, paid };
  const errors = attempted ? invoiceErrors(form) : [];
  const errOf = (field: string) => errors.find(e => e.field === field)?.message;
  const t = invoiceTotals(items, discountPct, taxPct, shipping, paid);
  const money = (n: number) => fmtMoney(n, currency);
  const { logUsageNow } = useLogUsageOnAction();

  const handleGenerate = async () => {
    // Validate first: a missing field must not use up a daily credit.
    setAttempted(true);
    const found = invoiceErrors(form);
    if (found.length) {
      toast.error(found.length === 1 ? found[0].message : `Please fix ${found.length} things before generating. The first is marked.`);
      setTimeout(() => { const el = document.getElementById(found[0].field); el?.scrollIntoView({ block: "center" }); el?.focus(); }, 0);
      return;
    }
    const allowed = await logUsageNow();
    if (!allowed) return;

    try {
      const pdf = await PDFDocument.create();
      const everything = [termsLine(terms), businessName, businessAddress, businessTaxId, clientName, clientAddress, invoiceNumber, invoiceDate, dueDate, notes, taxLabel,
        ...t.lines.map(l => l.description), "Total Subtotal Discount Shipping Amount paid Balance due (credit) Description Qty Unit Price Amount Bill To Notes Invoice Date Due Page Approved by Tax ID",
        money(0), "0123456789,.-%"].join("\n");
      const kit = await TextKit.create(pdf, everything);

      let page = pdf.addPage([PAGE_W, PAGE_H]);
      let y = 740;
      const COL = { qtyRight: 372, priceRight: 462, amountRight: RIGHT };
      const descW = 372 - 40 - LEFT; // description column width, leaving room for the quantity

      const tableHeader = () => {
        kit.draw(page, "Description", { x: LEFT, y, size: 10, bold: true });
        kit.drawRight(page, "Qty", { rightX: COL.qtyRight, y, size: 10, bold: true });
        kit.drawRight(page, "Unit price", { rightX: COL.priceRight, y, size: 10, bold: true });
        kit.drawRight(page, "Amount", { rightX: COL.amountRight, y, size: 10, bold: true });
        y -= 8;
        page.drawLine({ start: { x: LEFT, y }, end: { x: RIGHT, y }, thickness: 1, color: rgb(0.8, 0.8, 0.8) });
        y -= 16;
      };
      const newPage = (withHeader: boolean) => {
        page = pdf.addPage([PAGE_W, PAGE_H]);
        y = 740;
        kit.draw(page, `Invoice ${invoiceNumber} (continued)`, { x: LEFT, y, size: 10, color: GREY });
        y -= 24;
        if (withHeader) tableHeader();
      };
      const room = (needed: number, withHeader = true) => { if (y - needed < BOTTOM) newPage(withHeader); };

      kit.draw(page, "INVOICE", { x: LEFT, y, size: 24, bold: true });
      kit.drawRight(page, `Invoice #: ${invoiceNumber}`, { rightX: RIGHT, y: y + 14, size: 10 });
      kit.drawRight(page, `Date: ${invoiceDate}`, { rightX: RIGHT, y, size: 10 });
      if (dueDate) kit.drawRight(page, `Due: ${dueDate}`, { rightX: RIGHT, y: y - 14, size: 10 });
      const termsText = termsLine(terms);
      if (termsText) kit.drawRight(page, termsText, { rightX: RIGHT, y: y - (dueDate ? 28 : 14), size: 10 });
      y -= termsText ? 54 : 40;

      const block = (title: string, name: string, address: string, extra?: string) => {
        kit.draw(page, title, { x: LEFT, y, size: 9, bold: true, color: GREY });
        y -= 14;
        kit.draw(page, name, { x: LEFT, y, size: 12, bold: true });
        y -= 14;
        for (const l of kit.wrap(address, 10, 240)) { if (!l) continue; kit.draw(page, l, { x: LEFT, y, size: 10 }); y -= 13; }
        if (extra) { kit.draw(page, extra, { x: LEFT, y, size: 10 }); y -= 13; }
        y -= 10;
      };
      block("FROM", businessName, businessAddress, businessTaxId.trim() ? `Tax ID: ${businessTaxId.trim()}` : undefined);
      block("BILL TO", clientName, clientAddress);
      y -= 6;

      tableHeader();
      for (const l of t.lines) {
        const descLines = kit.wrap(l.credit ? `${l.description} (credit)` : l.description, 10, descW);
        room(descLines.length * 13 + 8);
        descLines.forEach((line, i) => kit.draw(page, line, { x: LEFT, y: y - i * 13, size: 10 }));
        kit.drawRight(page, String(l.qty), { rightX: COL.qtyRight, y, size: 10 });
        kit.drawRight(page, money(l.price), { rightX: COL.priceRight, y, size: 10 });
        kit.drawRight(page, money(l.amount), { rightX: COL.amountRight, y, size: 10 });
        y -= descLines.length * 13 + 6;
      }

      room(110, false);
      y -= 4;
      page.drawLine({ start: { x: 340, y }, end: { x: RIGHT, y }, thickness: 1, color: rgb(0.8, 0.8, 0.8) });
      y -= 16;
      const row = (label: string, value: string, bold = false, size = 10) => {
        kit.draw(page, label, { x: 340, y, size, bold });
        kit.drawRight(page, value, { rightX: RIGHT, y, size, bold });
        y -= size + 6;
      };
      if (t.discount > 0 || t.tax > 0 || t.shipping > 0) row("Subtotal", money(t.subtotal));
      if (t.discount > 0) row(`Discount (${discountPct}%)`, `-${money(t.discount)}`);
      if (t.tax > 0) row(`${taxLabel.trim() || "Tax"} (${taxPct}%)`, money(t.tax));
      if (t.shipping > 0) row("Shipping", money(t.shipping));
      row("Total", money(t.total), true, 12);
      if (t.paid > 0) {
        row("Amount paid", `-${money(t.paid)}`);
        row("Balance due", money(t.balanceDue), true, 12);
      }

      if (notes.trim()) {
        y -= 14;
        room(40, false);
        kit.draw(page, "Notes / payment instructions", { x: LEFT, y, size: 10, bold: true });
        y -= 15;
        for (const line of kit.wrap(notes.trim(), 10, RIGHT - LEFT)) {
          room(15, false);
          kit.draw(page, line, { x: LEFT, y, size: 10 });
          y -= 13;
        }
      }

      if (includeSignature) {
        y -= 30;
        room(50, false);
        kit.draw(page, "Approved by: _________________________", { x: LEFT, y, size: 10 });
        y -= 20;
        kit.draw(page, "Date: _____________", { x: LEFT, y, size: 10 });
      }

      // page numbers on multi-page invoices
      const pages = pdf.getPages();
      if (pages.length > 1) pages.forEach((p, i) => kit.drawRight(p, `Page ${i + 1} of ${pages.length}`, { rightX: RIGHT, y: 40, size: 8, color: GREY }));

      await kit.finish();
      const pdfBytes = await pdf.save();
      downloadBlob(new Blob([new Uint8Array(pdfBytes)], { type: "application/pdf" }), `${invoiceNumber.trim() || "invoice"}.pdf`);
      writeLast(invoiceNumber.trim());
      const note = kit.note();
      if (note) toast.warning(note); else toast.success(`Invoice generated! Next invoice number: ${nextInvoiceNumber(invoiceNumber)}.`);
    } catch (err) {
      console.error("Invoice PDF failed", err);
      toast.error("Couldn't build the PDF. Try removing unusual symbols from the text, or contact us if it keeps happening.");
    }
  };

  const errP = (field: string) => { const m = errOf(field); return m ? <p id={`${field}-err`} role="alert" className="text-xs text-destructive mt-1">{m}</p> : null; };
  const inv = (field: string) => ({ id: field, "aria-invalid": errOf(field) ? true : undefined, "aria-describedby": errOf(field) ? `${field}-err` : undefined, className: errOf(field) ? "border-destructive" : undefined });
  const lineCls = (field: string, base: string) => `${base} ${errOf(field) ? "border-destructive" : ""}`;

  return (
    <ToolContainer>
      <div className="max-w-xl mx-auto space-y-5">
        <div className="flex flex-wrap gap-2 justify-end">
          <Button variant="ghost" size="sm" className="gap-1.5" onClick={() => ask("sample")}><FlaskConical className="h-3.5 w-3.5" /> Try a sample</Button>
          <Button variant="ghost" size="sm" className="gap-1.5" onClick={() => ask("clear")}><Eraser className="h-3.5 w-3.5" /> Clear</Button>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div><Label htmlFor="inv-business">Your Business Name *</Label><Input {...inv("inv-business")} value={businessName} onChange={e => setBusinessName(e.target.value)} data-testid="inv-business" />{errP("inv-business")}</div>
          <div><Label htmlFor="inv-client">Client Name *</Label><Input {...inv("inv-client")} value={clientName} onChange={e => setClientName(e.target.value)} data-testid="inv-client" />{errP("inv-client")}</div>
          <div><Label htmlFor="inv-baddr">Your address (optional)</Label><Textarea id="inv-baddr" rows={2} value={businessAddress} onChange={e => setBusinessAddress(e.target.value)} /></div>
          <div><Label htmlFor="inv-caddr">Client address (optional)</Label><Textarea id="inv-caddr" rows={2} value={clientAddress} onChange={e => setClientAddress(e.target.value)} /></div>
        </div>
        <div><Label htmlFor="inv-taxid">Your tax ID (optional: EIN, VAT, GSTIN)</Label><Input id="inv-taxid" value={businessTaxId} onChange={e => setBusinessTaxId(e.target.value)} /></div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <div><Label htmlFor="inv-number">Invoice #</Label><Input id="inv-number" value={invoiceNumber} onChange={e => setInvoiceNumber(e.target.value)} /></div>
          <div><Label htmlFor="inv-date">Date</Label><Input {...inv("inv-date")} type="date" value={invoiceDate} onChange={e => changeInvoiceDate(e.target.value)} data-testid="inv-date" />{errP("inv-date")}</div>
          <div>
            <Label htmlFor="inv-terms">Payment terms</Label>
            <select id="inv-terms" className="border rounded px-2 py-2 w-full bg-background text-sm h-10" value={terms} onChange={e => changeTerms(e.target.value as PaymentTerms)}>
              <option value="custom">Custom date</option>
              <option value="receipt">Due on receipt</option>
              <option value="net15">Net 15</option>
              <option value="net30">Net 30</option>
              <option value="net60">Net 60</option>
            </select>
          </div>
          <div><Label htmlFor="inv-due">Due Date</Label><Input {...inv("inv-due")} type="date" value={dueDate} onChange={e => { setDueDate(e.target.value); setTerms("custom"); }} />{errP("inv-due")}</div>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <div>
            <Label htmlFor="inv-currency">Currency</Label>
            <select id="inv-currency" className="border rounded px-2 py-2 w-full bg-background text-sm h-10" value={currency} onChange={e => setCurrency(e.target.value)}>
              {CURRENCY_CHOICES.map(c => <option key={c.code} value={c.code}>{c.label}</option>)}
            </select>
          </div>
          <div><Label htmlFor="inv-discount">Discount (%)</Label><Input {...inv("inv-discount")} inputMode="decimal" value={discountPct} onChange={e => setDiscountPct(e.target.value)} />{errP("inv-discount")}</div>
          <div><Label htmlFor="inv-tax">Tax (%)</Label><Input {...inv("inv-tax")} inputMode="decimal" value={taxPct} onChange={e => setTaxPct(e.target.value)} data-testid="inv-tax" />{errP("inv-tax")}</div>
          <div><Label htmlFor="inv-taxlabel">Tax label</Label><Input id="inv-taxlabel" value={taxLabel} onChange={e => setTaxLabel(e.target.value)} placeholder="Tax" /></div>
        </div>

        <div className="space-y-2">
          <Label>Line Items</Label>
          {items.map((item, n) => (
            <div key={item.id} className="space-y-1">
              <div className="flex gap-2 items-start flex-wrap sm:flex-nowrap">
                <div className="flex-1 min-w-[10rem]"><Input {...inv(`inv-item-${item.id}-desc`)} aria-label={`Description, line ${n + 1}`} placeholder="Description" value={item.description} onChange={e => updateItem(item.id, "description", e.target.value)} data-testid="inv-desc" /></div>
                <Input {...inv(`inv-item-${item.id}-qty`)} aria-label={`Quantity, line ${n + 1}`} inputMode="decimal" placeholder="Qty" value={item.quantity} onChange={e => updateItem(item.id, "quantity", e.target.value)} className={lineCls(`inv-item-${item.id}-qty`, "w-16")} />
                <Input {...inv(`inv-item-${item.id}-price`)} aria-label={`Unit price, line ${n + 1}`} inputMode="decimal" placeholder="Price" value={item.unitPrice} onChange={e => updateItem(item.id, "unitPrice", e.target.value)} className={lineCls(`inv-item-${item.id}-price`, "w-24")} data-testid="inv-price" />
                <label className="flex items-center gap-1 text-xs h-10 cursor-pointer whitespace-nowrap">
                  <Checkbox checked={!!item.credit} onCheckedChange={(v) => setItems(prev => prev.map(i => (i.id === item.id ? { ...i, credit: v === true } : i)))} aria-label={`Credit line, line ${n + 1}`} /> Credit
                </label>
                <Button size="icon" variant="ghost" onClick={() => removeItem(item.id)} disabled={items.length === 1} aria-label={`Remove line item ${n + 1}`}>
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
              {errP(`inv-item-${item.id}-desc`)}{errP(`inv-item-${item.id}-qty`)}{errP(`inv-item-${item.id}-price`)}
              {item.credit && <p className="text-xs text-muted-foreground">Credit line: this amount is subtracted from the invoice.</p>}
            </div>
          ))}
          <Button variant="outline" size="sm" onClick={addItem} className="gap-1.5">
            <Plus className="h-3.5 w-3.5" /> Add Line Item
          </Button>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div><Label htmlFor="inv-shipping">Shipping / delivery (optional)</Label><Input {...inv("inv-shipping")} inputMode="decimal" value={shipping} onChange={e => setShipping(e.target.value)} />{errP("inv-shipping")}</div>
          <div><Label htmlFor="inv-paid">Amount paid / deposit (optional)</Label><Input {...inv("inv-paid")} inputMode="decimal" value={paid} onChange={e => setPaid(e.target.value)} />{errP("inv-paid")}</div>
        </div>

        <div><Label htmlFor="inv-notes">Notes / payment instructions (optional)</Label><Textarea id="inv-notes" rows={3} value={notes} onChange={e => setNotes(e.target.value)} placeholder="Payment instructions, bank details, payment terms, thank-you note" /></div>

        <div className="p-4 rounded-xl border bg-primary/5 text-right space-y-0.5">
          {(t.discount > 0 || t.tax > 0 || t.shipping > 0) && <p className="text-xs text-muted-foreground">Subtotal {money(t.subtotal)}{t.discount > 0 ? ` · Discount -${money(t.discount)}` : ""}{t.tax > 0 ? ` · ${taxLabel.trim() || "Tax"} ${money(t.tax)}` : ""}{t.shipping > 0 ? ` · Shipping ${money(t.shipping)}` : ""}</p>}
          <p className="text-xs text-muted-foreground">Total</p>
          <p className="text-2xl font-bold text-primary" data-testid="inv-total">{money(t.total)}</p>
          {t.paid > 0 && <p className="text-sm text-muted-foreground">Paid {money(t.paid)} · Balance due <strong className="text-foreground" data-testid="inv-balance">{money(t.balanceDue)}</strong></p>}
        </div>

        <div className="flex items-center gap-2">
          <Checkbox id="include-signature" checked={includeSignature} onCheckedChange={(v) => setIncludeSignature(v === true)} />
          <Label htmlFor="include-signature" className="font-normal cursor-pointer">Add a client approval / signature line (optional)</Label>
        </div>

        <Button onClick={handleGenerate} className="w-full gap-2" data-testid="inv-generate">
          <Download className="h-4 w-4" /> Generate & Download Invoice (PDF)
        </Button>
      </div>
      <AlertDialog open={confirm !== null} onOpenChange={(o) => { if (!o) setConfirm(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{confirm === "sample" ? "Replace your entries with a sample?" : "Clear this invoice?"}</AlertDialogTitle>
            <AlertDialogDescription>You can undo this right after from the message that appears.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => { const k = confirm; setConfirm(null); if (k === "sample") doSample(); else doClear(); }}>{confirm === "sample" ? "Load sample" : "Clear"}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <ToolInfoSection whatIsIt={TOOL_INFO_CONTENT.what} howToUse={TOOL_INFO_CONTENT.how} tip={TOOL_INFO_CONTENT.tip} />
    </ToolContainer>
  );
}
